/**
 * http 配置
 */

import axios, { AxiosRequestConfig } from 'axios'
import qs from 'qs'
import Store from 'store'
import _ from 'lodash'
import UserStore from '@stores/UserStore'
import { apiConstants } from '@const/apiConstants'
import config from './config'
import CryptoJS from 'crypto-js'
import { getParamsWithAuth } from './auth'
import { transform } from '@utils/intl_lang'
import IntlCommon from '@stores/IntlCommon'
import { getZhiniaoPrefix } from '@utils/SSOHelper'

export * from 'axios'
// 取消重复请求
const pending: Array<{
  url: string
  cancel: () => void
}> = []
const cancelToken = axios.CancelToken
const removePending = (c: AxiosRequestConfig) => {
  Object.keys(pending).forEach((p: any) => {
    const item: any = p
    const list: any = pending[p] || []
    if (list && list.url === c.url + '&request_type=' + c.method) {
      list.cancel()
      pending.splice(item, 1)
    }
  })
}

const service = axios.create(config)

// http 请求拦截器
service.interceptors.request.use(
  (c: AxiosRequestConfig) => {
    c.headers.Language = IntlCommon.browserLanguage

    let sid = UserStore.getSid() || Store.get('sessionInfo')?.sid
    const znHost = getZhiniaoPrefix()

    // 判断是否是SaaS接口，SaaS接口用SaaS的 sid
    const isSaas = c.url?.indexOf(znHost) === 0
    if (isSaas) {
      const liveUserInfo = Store.get('liveUserInfo') || {}
      sid = liveUserInfo.sid
    }

    // 直播接口更新sid
    if (c.url?.includes('/learn/app/clientapi/live/getToken.do')) {
      c.params.userName = sid
    }

    if (!_.get(c, 'params.noInterceptorCancel')) {
      removePending(c)
    }
    c.cancelToken = new cancelToken(c1 => {
      pending.push({ url: c.url + '&request_type=' + c.method, cancel: c1 })
    })

    const contentType = c.headers['Content-Type'] || c.headers.common.Accept

    if (c.method === 'post') {
      // 增加sid
      let data = c.data
      if (!c.headers.sid) {
        data = sid ? { ...c.data, sid } : { ...c.data }
      }
      // 判断不需要加-Language
      const noLanguage = c.url?.includes('/live/sdk/entryLiveRoom')
      if (noLanguage) {
        delete c.headers.Language
      }
      // 处理noInterceptorCancel
      if (_.get(c, 'params.noInterceptorCancel')) {
        c.params = _.omit(c.params, 'noInterceptorCancel')
      }
      // 处理clearHeaderMap
      if (!_.get(c.data, 'clearHeaderMap')) {
        // 平台标识99
        c.url = c.url?.includes('?')
          ? `${c.url}&os=${apiConstants.STR_OS}`
          : `${c.url}?os=${apiConstants.STR_OS}`
        c.headers.headerMap = getParamsWithAuth(c.url, data, contentType, c.params, isSaas)
      } else {
        data = _.omit(data, 'clearHeaderMap')
      }
      // 处理form表单参数格式
      if (
        c.headers['Content-Type'] ===
        'application/x-www-form-urlencoded;charset=UTF-8'
      ) {
        c.data = qs.stringify(data, { indices: false })
      } else if (c.headers['Content-Type'] === 'multipart/form-data') {
        c.data = c.data.file
      } else {
        c.data = data
      }
    } else {
      c.params = sid ? { ...c.params, sid } : { ...c.params }

      if (!_.get(c.params, 'clearHeaderMap')) {
        c.params = { ...c.params, os: apiConstants.STR_OS }
        c.headers.headerMap = getParamsWithAuth(c.url, c.params, contentType, null, isSaas)
      } else {
        c.params = _.omit(c.params, 'clearHeaderMap')
      }
    }
    return c
  },
  error => {
    return Promise.reject(error)
  }
)

// http 响应拦截器
service.interceptors.response.use(
  (res: any) => {
    if (res.headers.isencrypt) {
      // 加密数据
      const resData = res.headers.isencrypt === 'true' ? JSON.parse(decrypt(res.data)) : JSON.parse(res.data)
      return resData
    }
    const data = res.data || {}
    const code = +res.data?.code
    if (code === -4) {
      // 银行直播间、游客场景、中台查看回放
      const paths = ['bankliveroom', 'touristlive', 'replayTips']
      const route = location.href.split('?')[1]
      // 运营充值场景
      const isToUp = route && route.includes('/#/mili-top-up')
      // 单点登录前登出操作场景
      const isSingleRoute = location.href.includes('/#/singleLogin')
      const isSingleType = Store.get('isSingleLogin')
      const isSingleLogin = isSingleRoute || isSingleType
      // 第三方跳转观看直播；
      const isThirdViewLive = route && route.includes('viewFrom')
      // 非/路径
      const hashPath = location.hash.split('/')[1]
      const jumpLinkList = [
        /(course|exam)Detail\/[\s\S]+/,
        /myTrainingDetail\/[\s\S]+/,
        /studyDetail\/[\s\S]+/,
        /liveroom\/[\s\S]+/,
        /myQuestionnaireDetail\/[\s\S]+/,
        /homework\/fromUser\/[\s\S]+/,
        /practiceTrain\/[\s\S]+/,
        /knowledgeLibDetail\/[\s\S]+/,
        /filePreview\/[\s\S]+/,
        /project\/learnCard/,
      ]
      const needJumpLink = jumpLinkList.find(item => location.hash.match(item))
      if (hashPath === 'liveroom' && isThirdViewLive) {
        const [directRoute, query] = location.hash.split('#/')[1].split('?')
        location.hash = `/login?route=/${directRoute}&${query}`
      } else if (needJumpLink && location.hash.indexOf('login') === -1) {
        let [directRoute, query] = location.hash.split('#/')[1].split('?')
        if (location.hash && location.hash.match(/examDetail\/[\s\S]+/)) { // 考试详情跳回登录页不需要带上次考试详情的数据
          query = ''
        }
        location.hash = `/login?route=/${query ? `${directRoute}&${query}` : `${directRoute}`}`
      } else if (
        !paths.includes(hashPath) &&
        !isToUp &&
        !isSingleLogin &&
        !isThirdViewLive &&
        !needJumpLink
      ) {
        location.hash = '/'
      }
      Store.remove('sessionInfo')
      Store.remove('refreshTime')
      localStorage.removeItem('timeInRoom')
      localStorage.removeItem('firstEnterTime')
      localStorage.removeItem('opened_treasure_boxes')
      if (UserStore.isLogin) {
        // '为了您的账户安全，请重新登录'
        data.message = transform('login_again_please')
      }
      UserStore.initState()
    } else if (code === 41766 || code === 41767) {
      // 降级码: 41766, 限流码: 41767
      data.code = -4
    }

    if (isNaN(code)) {
      return data
    }
    return { ...data, code }
  },
  error => {
    return Promise.reject(error)
  }
)



const decrypt = (data: string) => {
  // AES 秘钥
  const AesKey = '3c26d0badca24176'
  // AES-128-CBC偏移量
  const CBCIV = '39934fc88c73430e'
  // 加密选项
  const CBCOptions = {
    iv: CryptoJS.enc.Utf8.parse(CBCIV),
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7
  }
  const key = CryptoJS.enc.Utf8.parse(AesKey)
  const decrypt = CryptoJS.AES.decrypt(
    data,
    key,
    CBCOptions
  )
  return CryptoJS.enc.Utf8.stringify(decrypt).toString()
}

export default service
